// @ai-generated 2026-07-28T15:01:24+00:00 by niklas.eicker@tngtech.com using Claude Opus 5
import { initialRequisitionSorting, portalRequisitionColumns } from '@Constants/TableColumns';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission, useProfileName } from '@Contexts/appContext';
import { useCurrentPartnership, useSession } from '@Contexts/session';
import { useNextRequisition, useRecentRequisitions } from '@Hooks/Orders';
import { usePortalServiceLabels } from '@Hooks/Stammdaten/usePortalLabels';
import { type ApiRequisition } from '@Model';
import {
    ActionTile,
    Button,
    FlexColumn,
    InlineBanner,
    OnboardingPanel,
    renderDateTime,
    ScrollContainer,
    Table,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconChartBar, IconFileInvoice, IconTruck } from '@tabler/icons-react';
import { getRelativeDayLabel } from '@utils/relativeDayLabel';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import classes from './FrameHome.module.css';
import { nextRequisitionTitle } from './portalNextRequisition';
import { PORTAL_ORDER_STEPS } from './portalOrderSteps';

const RECENT_REQUISITION_COUNT = 5;

/** Landing page of the customer portal: what happens next, what to do, and the last few orders. */
export function FrameHome() {
    const i18n = useTranslate('');
    const navigate = useNavigate();
    const hasPermission = useHasPermission();
    const profileName = useProfileName();
    const {
        current: { subpath },
    } = useSession();
    const operatorName = useCurrentPartnership()?.mandant_display;

    const canOrder = hasPermission(Permission.AuftragsbearbeitungRequisitionsCreate);
    const canSeeRequisitions = hasPermission(Permission.AuftragsbearbeitungRequisitionsRead);
    const canSeeInvoices = hasPermission(Permission.BillingInvoiceSnapshotsRead);

    const { requisitions, isLoading } = useRecentRequisitions(RECENT_REQUISITION_COUNT, canSeeRequisitions);

    const { ensure: ensureMaterialNames, labelOf: materialOf } = usePortalServiceLabels();
    useEffect(() => {
        ensureMaterialNames(
            (requisitions ?? []).flatMap((requisition) => requisition.positions ?? []).map((p) => p.service),
        );
    }, [requisitions, ensureMaterialNames]);

    const columns = useMemo(() => portalRequisitionColumns(materialOf), [materialOf]);
    const { nextRequisition } = useNextRequisition(canSeeRequisitions);

    const goTo = useCallback((target: string) => navigate(`/${subpath}${target}`), [navigate, subpath]);

    const openRequisition = useCallback(
        (requisition: ApiRequisition) => {
            goTo(`/requisitions/${requisition.id}`);
            return true;
        },
        [goTo],
    );

    const firstName = profileName?.split(' ')[0] ?? profileName;

    return (
        <ScrollContainer>
            <FlexColumn gap="lg" align="stretch" className={classes.content}>
                <div>
                    <h1 className={classes.greeting}>
                        {i18n('Guten Tag')}, {firstName}
                    </h1>
                    {operatorName && (
                        <p className={classes.welcome}>
                            {i18n('Willkommen im Kundenportal von')} <strong>{operatorName}</strong>
                        </p>
                    )}
                </div>

                {nextRequisition && (
                    <InlineBanner
                        icon={<IconTruck size={18} />}
                        title={`${i18n(nextRequisitionTitle(nextRequisition))}: ${describeDate(nextRequisition)}`}
                        description={[nextRequisition.site?.address?.display_string, nextRequisition.number]
                            .filter(Boolean)
                            .join(' · ')}
                        action={
                            <Button appearance="transparent" onClick={() => openRequisition(nextRequisition)}>
                                {i18n('Details')} →
                            </Button>
                        }
                    />
                )}

                <div className={classes.tiles}>
                    {canOrder && (
                        <ActionTile
                            variant="primary"
                            icon={<IconTruck size={22} />}
                            title={i18n('Container bestellen')}
                            description={i18n(
                                'Abholung oder Lieferung in wenigen Schritten beauftragen — Leistungsort, Positionen und Termin wählen.',
                            )}
                            actionLabel={`${i18n('Jetzt bestellen')} →`}
                            onClick={() => goTo('/requisitions/new')}
                        />
                    )}
                    {canSeeInvoices && (
                        <ActionTile
                            icon={<IconFileInvoice size={20} />}
                            title={i18n('Rechnungen')}
                            description={i18n('Rechnungen einsehen und als PDF herunterladen.')}
                            actionLabel={`${i18n('Zu den Rechnungen')} →`}
                            onClick={() => goTo('/invoices')}
                        />
                    )}
                    <ActionTile
                        locked
                        icon={<IconChartBar size={20} />}
                        title={i18n('Abfallbilanz')}
                        description={i18n('Entsorgte Mengen nach Material und Zeitraum auswerten.')}
                        badge={{ children: i18n('Bald verfügbar') }}
                    />
                </div>

                {canSeeRequisitions && !isLoading && requisitions && (
                    <div>
                        <div className={classes.sectionHeader}>
                            <h2 className={classes.sectionTitle}>{i18n('Letzte Bestellungen')}</h2>
                            <Button appearance="transparent" onClick={() => goTo('/requisitions')}>
                                {i18n('Alle Bestellungen')} →
                            </Button>
                        </div>
                        {requisitions.length === 0 ? (
                            <OnboardingPanel
                                icon={<IconTruck size={42} stroke={1.6} />}
                                title={i18n('Noch keine Bestellungen')}
                                description={i18n(
                                    'Starten Sie jetzt mit Ihrer ersten Bestellung — es dauert nur wenige Minuten.',
                                )}
                                action={
                                    canOrder ? (
                                        <Button type="rounded" onClick={() => goTo('/requisitions/new')}>
                                            {i18n('Container bestellen')}
                                        </Button>
                                    ) : undefined
                                }
                                steps={PORTAL_ORDER_STEPS}
                                stepsLabel={i18n('Bestellschritte')}
                            />
                        ) : (
                            <Table<ApiRequisition>
                                name="PortalRecentRequisitions"
                                columns={columns}
                                items={requisitions}
                                initialSorting={initialRequisitionSorting}
                                onSelect={openRequisition}
                                disableTopToolbar
                                minimal
                            />
                        )}
                    </div>
                )}
            </FlexColumn>
        </ScrollContainer>
    );
}

function describeDate(requisition: ApiRequisition): string {
    return (
        getRelativeDayLabel(requisition.date_from) ??
        (requisition.date_from ? renderDateTime(requisition.date_from, 'date') : '')
    );
}
